import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/lib/auth";
import { useAnimatedNumber } from "@/lib/useAnimatedNumber";
import ExecutiveBrief from "@/components/ExecutiveBrief";
import MonthOnMonthChart from "@/components/MonthOnMonthChart";
import PendingSinceTable from "@/components/PendingSinceTable";
import { ClipboardText, CheckCircle, Clock, Warning, ChartLineUp } from "@phosphor-icons/react";

function KPI({ label, value, tone = "blue", suffix = "", icon, testId, decimals = 0 }) {
  const v = useAnimatedNumber(value || 0);
  const display = decimals ? v.toFixed(decimals) : Math.round(v);
  const tones = {
    blue: "bg-[#0B2A5B] text-white",
    red: "bg-red-50 text-red-800 border border-red-200",
    amber: "bg-amber-50 text-amber-800 border border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border border-emerald-200",
    slate: "bg-slate-50 text-slate-800 border border-slate-200",
  };
  return (
    <div className={`rounded-md p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-75 mb-1">
        {icon} {label}
      </div>
      <div className="text-3xl font-semibold tabular-nums" style={{ fontFamily: "Geist" }}>
        {display}{suffix}
      </div>
    </div>
  );
}

export default function FounderDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    if (!user?.company_id) return;
    if (refresh) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await api.get(`/analytics/founder-overview?company_id=${user.company_id}${refresh ? "&refresh=true" : ""}`);
      setData(r.data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [user?.company_id]);

  const totals = data?.totals || {};

  const header = (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Geist" }}>Founder's View</h1>
      <p className="text-sm text-slate-500 mt-0.5">
        Your team's performance against the open audit observations — trends, oldest pending items, and what to discuss next.
      </p>
    </div>
  );

  if (loading) {
    return (
      <AppLayout header={header}>
        <div className="text-sm text-slate-500" data-testid="founder-loading">Loading executive view...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout header={header}>
      <div className="space-y-6" data-testid="founder-dashboard">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Open Items" value={totals.open || 0} tone="blue" icon={<ClipboardText size={11} />} testId="kpi-open" />
          <KPI label="High Risk Open" value={totals.high_open || 0} tone="red" icon={<Warning size={11} />} testId="kpi-high" />
          <KPI label="Resolution Rate" value={totals.resolution_rate || 0} suffix="%" tone="emerald" icon={<CheckCircle size={11} />} testId="kpi-resolution-rate" decimals={1} />
          <KPI label="Avg Resolution Days" value={totals.avg_resolution_days || 0} tone="slate" icon={<Clock size={11} />} testId="kpi-avg-days" decimals={1} />
          <KPI label="Oldest Open (days)" value={totals.oldest_open_days || 0} tone="amber" icon={<Clock size={11} />} testId="kpi-oldest" />
        </div>

        <ExecutiveBrief
          brief={data?.ai_brief}
          audience="Founder"
          onRefresh={() => load(true)}
          refreshing={refreshing}
          cached={data?._cached}
        />

        <div>
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800">
            <ChartLineUp size={14} /> Month-on-month Engagement
          </div>
          <MonthOnMonthChart series={data?.month_series || []} />
        </div>

        <PendingSinceTable
          items={data?.pending_since || []}
          hrefBuilder={(it) => `/client/observations/${it.id}`}
        />

        {!!(data?.top_themes && data.top_themes.length) && (
          <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="top-themes">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-800">Recurring Themes</h4>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">By volume</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.top_themes.map((t) => (
                <span
                  key={t.area}
                  className="text-xs px-2 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700"
                  data-testid={`theme-${t.area}`}
                >
                  {t.area} <span className="opacity-60">· {t.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
